/**
 * ScriptBlocker, prior-consent script interception.
 *
 * Rewrites non-essential <script> tags to type="text/plain" before they
 * execute, then re-activates them after consent is granted.
 *
 * Must be injected as early as possible in <head>.
 *
 * A script element the browser has already prepared (one that was in the DOM
 * before intercept() ran, or that a scan-derived pattern only matched after it
 * was inserted) has executed or is going to, and rewriting its type afterwards
 * changes nothing. Those are labelled with data-pb-executed="true" and are
 * never reactivated, because cloning one on consent would run the tracker a
 * second time.
 */

import { FRAMEWORK_SRC_PATTERNS } from "../../../../supabase/functions/_shared/frameworkAssets.js";

export type ConsentCategory = "analytics" | "advertising" | "functional" | "social";

// Known tracking script patterns mapped to consent categories
const SCRIPT_PATTERNS: Array<{ pattern: RegExp; category: ConsentCategory }> = [
  // Analytics
  { pattern: /google-analytics\.com|googletagmanager\.com\/gtag/i, category: "analytics" },
  // The Google Tag Manager container. It loads whatever the container holds,
  // most of it analytics and advertising, so it waits for analytics consent
  // unless the embed opted into Consent Mode (see tagManagerAllowed).
  { pattern: /googletagmanager\.com\/gtm\.js/i, category: "analytics" },
  { pattern: /clarity\.ms/i, category: "analytics" },
  { pattern: /hotjar\.com/i, category: "analytics" },
  { pattern: /mouseflow\.com/i, category: "analytics" },
  { pattern: /fullstory\.com/i, category: "analytics" },
  { pattern: /segment\.com|segment\.io/i, category: "analytics" },
  { pattern: /mixpanel\.com/i, category: "analytics" },
  { pattern: /amplitude\.com/i, category: "analytics" },
  { pattern: /matomo\.|piwik\./i, category: "analytics" },
  // Advertising
  { pattern: /connect\.facebook\.net|fbevents\.js/i, category: "advertising" },
  { pattern: /doubleclick\.net|googlesyndication/i, category: "advertising" },
  { pattern: /ads\.twitter\.com|static\.ads-twitter\.com/i, category: "advertising" },
  { pattern: /snap\.licdn\.com|ads\.linkedin\.com/i, category: "advertising" },
  { pattern: /sc-static\.net|snapchat/i, category: "advertising" },
  { pattern: /bat\.bing\.com/i, category: "advertising" },
  { pattern: /tiktok\.com|analytics\.tiktok/i, category: "advertising" },
  { pattern: /criteo/i, category: "advertising" },
  { pattern: /outbrain\.com/i, category: "advertising" },
  { pattern: /taboola\.com/i, category: "advertising" },
  // Social
  { pattern: /platform\.twitter\.com|twitter\.com\/widgets/i, category: "social" },
  { pattern: /connect\.facebook\.net\/.*\/sdk/i, category: "social" },
  { pattern: /apis\.google\.com\/js\/platform/i, category: "social" },
  { pattern: /static\.addtoany\.com/i, category: "social" },
  // Functional / Chat
  { pattern: /intercom\.io\/widget/i, category: "functional" },
  { pattern: /js\.intercomcdn\.com/i, category: "functional" },
  { pattern: /widget\.drift\.com/i, category: "functional" },
  { pattern: /jivosite\.com/i, category: "functional" },
];

// Known tracking patterns found in inline scripts (textContent matching)
const INLINE_PATTERNS: Array<{ pattern: RegExp; category: ConsentCategory }> = [
  // Google Analytics / GTM inline init
  { pattern: /gtag\s*\(\s*['"]config['"]/i, category: "analytics" },
  { pattern: /GoogleAnalyticsObject/i, category: "analytics" },
  { pattern: /ga\s*\(\s*['"]create['"]/i, category: "analytics" },
  { pattern: /dataLayer\.push\s*\(\s*\{/i, category: "analytics" },
  // The standard untagged GTM container snippet:
  // (function(w,d,s,l,i){w[l].push({'gtm.start': ...});...})(window,document,'script','dataLayer','GTM-XXXX')
  { pattern: /['"]gtm\.start['"]/i, category: "analytics" },
  // Facebook Pixel inline init
  { pattern: /fbq\s*\(\s*['"]init['"]/i, category: "advertising" },
  // LinkedIn Insight
  { pattern: /_linkedin_partner_id/i, category: "advertising" },
  // TikTok Pixel
  { pattern: /ttq\.load\s*\(/i, category: "advertising" },
  // Hotjar
  { pattern: /hj\s*\(\s*['"]init['"]/i, category: "analytics" },
  // Criteo
  { pattern: /criteo_q\.push/i, category: "advertising" },
  // Mixpanel
  { pattern: /mixpanel\.init\s*\(/i, category: "analytics" },
  // Intercom
  { pattern: /Intercom\s*\(\s*['"]boot['"]/i, category: "functional" },
];

/**
 * Inline scripts that belong to the host page's own framework runtime.
 *
 * These carry hydration payloads (React Server Component flight rows, Nuxt /
 * SvelteKit / Remix state) whose serialised page content can contain arbitrary
 * text, including tracker snippets quoted as *data*. Rewriting one to
 * type="text/plain" drops a fragment of the stream, and every framework here
 * concatenates fragments, so the surviving rows no longer parse:
 * "SyntaxError: Expected ':' after property name in JSON".
 *
 * Never block these: they are first-party page infrastructure, not trackers.
 */
const FRAMEWORK_INLINE_PATTERNS: RegExp[] = [
  // Next.js App Router: RSC flight rows: self.__next_f.push([1,"..."])
  /self\s*\.\s*__next_f\b/,
  /\b__next_f\s*(?:\.push\b|=)/,
  // Next.js Pages Router hydration payload + next/script inline queue
  /__NEXT_DATA__/,
  /self\s*\.\s*__next_s\b/,
  /__NEXT_LOADED_PAGES__|__NEXT_P\b/,
  // React 18/19 streaming + Suspense runtime emitted inline during SSR, as
  // either the runtime definition ($RC=function(…)) or a call ($RC("B:0",…)).
  /\$R[BCSTMX]\s*(?:=\s*function\b|\(\s*["'])/,
  /self\s*\.\s*\$R[A-Z]\b/,
  // Bundler runtimes
  /__turbopack|__webpack_require__|webpackChunk|webpackJsonp/,
  // Nuxt / Vue
  /window\s*\.\s*__NUXT__|__unctx__|__NUXT_DATA__/,
  // SvelteKit
  /__sveltekit_/,
  // Remix / React Router
  /__remixContext|__remixRouteModules|__remixManifest/,
  // Gatsby
  /window\s*\.\s*___gatsby|___chunkMapping/,
  // Astro islands
  /astro-island/,
];

/**
 * Script `type` values the browser actually executes. Anything else (JSON
 * payloads, import maps, speculation rules, client-side templates) is inert
 * data that can quote tracker code without running it, so it is never blocked.
 * An empty/absent type means classic JavaScript.
 */
const EXECUTABLE_SCRIPT_TYPES = new Set([
  "",
  "module",
  "text/javascript",
  "application/javascript",
  "text/ecmascript",
  "application/ecmascript",
  "text/jscript",
  "text/babel",
  "module-shim",
]);

/**
 * First-party framework asset paths (FRAMEWORK_SRC_PATTERNS, imported above).
 * A scan-derived pattern (see addPatterns) can be broad enough to match one of
 * these; blocking the app's own runtime chunks would take the page down, so
 * they are excluded unconditionally. The list is shared with the scan's embed
 * load-order finding, which must not count these as running before Beacon, so
 * it lives in one file both import.
 */

/** The Tag Manager patterns that Consent Mode embeds deliberately let through. */
const TAG_MANAGER_SRC = /googletagmanager\.com\/gtm\.js/i;
const TAG_MANAGER_INLINE = /['"]gtm\.start['"]/i;

export interface BlockPattern {
  pattern: RegExp;
  category: ConsentCategory;
}

export interface ScriptBlockerOptions {
  /**
   * True when the embed opted into Google Consent Mode (data-gcm), so denied
   * defaults are installed before any tag runs. The Tag Manager container is
   * then let through, because Consent Mode is how it is meant to wait for
   * consent, and blocking it would break the cookieless pings Consent Mode
   * exists for. Without Consent Mode nothing tells the container to wait, so it
   * is held until analytics consent like any other tracker.
   */
  tagManagerAllowed?: boolean;
}

export class ScriptBlocker {
  private blocked: Array<{ element: HTMLScriptElement; category: ConsentCategory }> = [];
  private observer: MutationObserver | null = null;
  private restoreDomHooks: (() => void) | null = null;
  // Live consent state so enforcement can continue after the first decision:
  // a tracker injected later in a non-consented category stays blocked.
  private consented: Set<ConsentCategory> = new Set();
  // Instance pattern lists so scan-derived per-domain patterns can be merged in.
  private scriptPatterns: BlockPattern[] = [...SCRIPT_PATTERNS];
  private inlinePatterns: BlockPattern[] = [...INLINE_PATTERNS];
  private patternKeys: Set<string> = new Set();
  private tagManagerAllowed: boolean;

  constructor(options: ScriptBlockerOptions = {}) {
    this.tagManagerAllowed = options.tagManagerAllowed === true;
    if (this.tagManagerAllowed) {
      this.scriptPatterns = this.scriptPatterns.filter((p) => p.pattern.source !== TAG_MANAGER_SRC.source);
      this.inlinePatterns = this.inlinePatterns.filter((p) => p.pattern.source !== TAG_MANAGER_INLINE.source);
    }
  }

  /**
   * Start blocking non-essential scripts.
   * Call this as early as possible (before DOM is ready).
   */
  intercept(): void {
    this.installDomHooks();

    // Scripts already in the DOM have been prepared by the browser: they ran,
    // or (async, defer, module) they will run whatever their type says now.
    // Label the trackers among them so they are never replayed on consent.
    document.querySelectorAll<HTMLScriptElement>("script").forEach((el) => {
      this.maybeBlock(el, true);
    });

    // Observe future script insertions
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLScriptElement) {
            this.maybeBlock(node);
          }
        });
      }
    });

    this.observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Release scripts for consented categories.
   */
  release(consentedCategories: Set<ConsentCategory>): void {
    // Record the live consent state so future insertions are gated correctly.
    this.consented = new Set(consentedCategories);
    const remaining: typeof this.blocked = [];
    const processed = new Set<HTMLScriptElement>();

    for (const { element, category } of this.blocked) {
      processed.add(element);
      if (consentedCategories.has(category)) {
        this.reactivate(element);
      } else {
        remaining.push({ element, category });
      }
    }

    document
      .querySelectorAll<HTMLScriptElement>('script[type="text/plain"][data-pb-category]')
      .forEach((element) => {
        if (processed.has(element)) return;
        // Already executed before we could stop it: replaying it would run
        // the tracker twice.
        if (element.dataset["pbExecuted"] === "true") return;
        const category = element.dataset["pbCategory"] as ConsentCategory | undefined;
        if (!category || !consentedCategories.has(category)) return;
        this.reactivate(element);
      });

    this.blocked = remaining;
  }

  /**
   * Stop observing the DOM.
   */
  stop(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.restoreDomHooks?.();
    this.restoreDomHooks = null;
  }

  private installDomHooks(): void {
    if (this.restoreDomHooks) return;

    const blocker = this;
    // Every insertion path that prepares a script on insertion must pass
    // through maybeBlock first. One that only the MutationObserver sees has
    // already started executing by the time the observer runs.
    const inspect = (node: unknown): void => {
      if (node instanceof HTMLScriptElement) {
        blocker.maybeBlock(node);
      } else if (node instanceof DocumentFragment) {
        node.querySelectorAll("script").forEach((el) => blocker.maybeBlock(el));
      }
    };

    const originalAppendChild = Node.prototype.appendChild;
    const originalInsertBefore = Node.prototype.insertBefore;
    const originalReplaceChild = Node.prototype.replaceChild;
    const elementProto = Element.prototype;
    const originalAppend = elementProto.append;
    const originalPrepend = elementProto.prepend;
    const originalBefore = elementProto.before;
    const originalAfter = elementProto.after;
    const originalReplaceWith = elementProto.replaceWith;
    const originalInsertAdjacentElement = elementProto.insertAdjacentElement;

    Node.prototype.appendChild = function<T extends Node>(node: T): T {
      inspect(node);
      return originalAppendChild.call(this, node) as T;
    };

    Node.prototype.insertBefore = function<T extends Node>(node: T, child: Node | null): T {
      inspect(node);
      return originalInsertBefore.call(this, node, child) as T;
    };

    Node.prototype.replaceChild = function<T extends Node>(node: Node, child: T): T {
      inspect(node);
      return originalReplaceChild.call(this, node, child) as T;
    };

    elementProto.append = function(...nodes: (Node | string)[]): void {
      nodes.forEach(inspect);
      return originalAppend.apply(this, nodes);
    };
    elementProto.prepend = function(...nodes: (Node | string)[]): void {
      nodes.forEach(inspect);
      return originalPrepend.apply(this, nodes);
    };
    elementProto.before = function(...nodes: (Node | string)[]): void {
      nodes.forEach(inspect);
      return originalBefore.apply(this, nodes);
    };
    elementProto.after = function(...nodes: (Node | string)[]): void {
      nodes.forEach(inspect);
      return originalAfter.apply(this, nodes);
    };
    elementProto.replaceWith = function(...nodes: (Node | string)[]): void {
      nodes.forEach(inspect);
      return originalReplaceWith.apply(this, nodes);
    };
    elementProto.insertAdjacentElement = function(where: InsertPosition, element: Element): Element | null {
      inspect(element);
      return originalInsertAdjacentElement.call(this, where, element);
    };

    this.restoreDomHooks = () => {
      Node.prototype.appendChild = originalAppendChild;
      Node.prototype.insertBefore = originalInsertBefore;
      Node.prototype.replaceChild = originalReplaceChild;
      elementProto.append = originalAppend;
      elementProto.prepend = originalPrepend;
      elementProto.before = originalBefore;
      elementProto.after = originalAfter;
      elementProto.replaceWith = originalReplaceWith;
      elementProto.insertAdjacentElement = originalInsertAdjacentElement;
    };
  }

  /**
   * Merge additional block patterns (a per-domain manifest built from scan
   * results) and re-scan the DOM so trackers the scanner found but the static
   * list misses are also blocked when unconsented.
   *
   * Call it before intercept() whenever the patterns are known synchronously
   * (the cached manifest), so they are in force for every script the page
   * inserts. A pattern that only arrives later cannot stop a script already in
   * the DOM: that script is labelled as executed and never replayed.
   */
  addPatterns(patterns: BlockPattern[]): void {
    const fresh = patterns.filter((p) => {
      const key = `${p.category}:${p.pattern.source}`;
      if (this.patternKeys.has(key)) return false;
      this.patternKeys.add(key);
      return true;
    });
    if (!fresh.length) return;
    this.scriptPatterns.push(...fresh);
    document.querySelectorAll<HTMLScriptElement>("script").forEach((el) => {
      this.maybeBlock(el, true);
    });
  }

  /**
   * @param alreadyPrepared the element is already in the document, so the
   *   browser has prepared it and rewriting its type can no longer stop it.
   */
  private maybeBlock(el: HTMLScriptElement, alreadyPrepared = false): void {
    if (el.dataset["pbReleased"] === "true") return;
    if (el.dataset["pbExecuted"] === "true") return;
    if (el.type === "text/plain") return; // Already blocked
    const category = this.classify(el);
    if (!category) return;
    // Consented categories are never blocked, even for scripts inserted after
    // the first decision, so enforcement can stay live without stopping.
    if (this.consented.has(category)) return;
    if (alreadyPrepared) {
      this.markExecuted(el, category);
      return;
    }
    this.blockScript(el, category);
  }

  /**
   * Label a tracker that ran before it could be blocked. Its type is rewritten
   * like a blocked script's, so the page reads the same either way and the
   * load-order probe can count it by data-pb-original-type, but it is never
   * added to the release list.
   */
  private markExecuted(el: HTMLScriptElement, category: ConsentCategory): void {
    const originalType = el.getAttribute("type") || "text/javascript";
    el.dataset["pbOriginalType"] = originalType;
    el.dataset["pbExecuted"] = "true";
    el.dataset["pbCategory"] = category;
    el.type = "text/plain";
  }

  /** Classify a script into a consent category, or null if not one we block. */
  private classify(el: HTMLScriptElement): ConsentCategory | null {
    // An explicit data-pb-category is the site owner declaring what the tag is,
    // so it outranks every heuristic below, including the framework guard. A
    // hand-tagged tracker whose bundled source happens to mention webpackChunk
    // must still be blocked.
    const dataCategory = el.dataset["pbCategory"];
    if (dataCategory) {
      const isBlockable =
        dataCategory === "analytics" ||
        dataCategory === "advertising" ||
        dataCategory === "functional" ||
        dataCategory === "social";
      return isBlockable ? (dataCategory as ConsentCategory) : null;
    }

    // Everything else: the page's own infrastructure is off limits. Touching it
    // breaks the host site rather than a tracker.
    if (ScriptBlocker.isPageInfrastructure(el)) return null;

    const src = el.src || el.getAttribute("src") || "";
    if (src) {
      for (const { pattern, category } of this.scriptPatterns) {
        if (pattern.test(src)) return category;
      }
      return null;
    }
    const content = el.textContent || "";
    if (content.length > 0) {
      for (const { pattern, category } of this.inlinePatterns) {
        if (pattern.test(content)) return category;
      }
    }
    return null;
  }

  /**
   * True when the script is part of the host page itself rather than a
   * third-party tag: a framework hydration payload, a bundler runtime, a
   * first-party framework asset, or an inert non-executable payload.
   *
   * Inline tracker detection matches on textContent, and a framework payload
   * *serialises the whole page*, so a site whose markup merely mentions
   * gtag('config', …) or fbq('init', …) would otherwise have its hydration
   * stream rewritten to text/plain and corrupted.
   *
   * Consulted only for untagged scripts: an explicit data-pb-category is
   * checked first in classify() and always wins. Exported for the regression
   * suite.
   */
  static isPageInfrastructure(el: HTMLScriptElement): boolean {
    const src = el.src || el.getAttribute("src") || "";
    if (src) {
      return FRAMEWORK_SRC_PATTERNS.some((pattern) => pattern.test(src));
    }

    // Inert payloads (application/json, importmap, speculationrules, client
    // templates …) never execute, so they can never be a tracker, but they can
    // quote one.
    const type = (el.getAttribute("type") || "").trim().toLowerCase().split(";")[0]!;
    if (!EXECUTABLE_SCRIPT_TYPES.has(type)) return true;

    const content = el.textContent || "";
    if (!content) return false;
    return FRAMEWORK_INLINE_PATTERNS.some((pattern) => pattern.test(content));
  }

  private blockScript(el: HTMLScriptElement, category: ConsentCategory): void {
    if (el.type === "text/plain") return; // Already blocked
    const originalType = el.getAttribute("type") || "text/javascript";
    el.dataset["pbOriginalType"] = originalType;
    el.type = "text/plain";
    el.dataset["pbCategory"] = category;
    this.blocked.push({ element: el, category });
  }

  private reactivate(el: HTMLScriptElement): void {
    const src = el.getAttribute("src");
    const originalType = el.dataset["pbOriginalType"] || "text/javascript";
    // Must clone and re-insert to trigger execution
    const newScript = document.createElement("script");
    newScript.type = originalType;
    newScript.dataset["pbReleased"] = "true";
    if (src) newScript.src = src;
    else newScript.textContent = el.textContent;

    // Copy attributes
    Array.from(el.attributes).forEach((attr) => {
      if (!["type", "src", "data-pb-category", "data-pb-original-type", "data-pb-released", "data-pb-executed"].includes(attr.name)) {
        newScript.setAttribute(attr.name, attr.value);
      }
    });

    el.parentNode?.replaceChild(newScript, el);
  }
}
